import {
  Box3,
  BufferGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  MeshBasicMaterial,
  PlaneGeometry,
  Texture,
  Vector3
} from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VoxelGridCalculator } from '../../utils/voxel-grid-calculator';
import { calculateBoundingBox } from '../helpers';
import { FRAME_AXIS_COLORS } from '../engine.constants';
import { EngineContext } from './engine-context';

/** A plane across the design parts, normal to `axis`, at `location` in UCS-frame coordinates. */
export interface MillingDepthPlaneSpec {
  axis: 0 | 1 | 2;
  location: number;
}

/** Turns the design parts' UCS-frame bounds into the planes to draw. */
export type MillingDepthPlaneResolver = (bounds: Box3) => MillingDepthPlaneSpec[];

/**
 * Manages drill bit visualization and symmetry planes.
 * Extracted from Engine to reduce class size.
 */
export class DrillBitsManager {
  constructor(private ctx: EngineContext) {}

  generateSymmetryPlanes(maxSize: number, center: Vector3, size: Vector3): void {
    for (const key of ['xy', 'xz', 'yz'] as const) {
      const group = new Group();
      group.visible = false;
      this.ctx.ucsFrame.add(group);
      this.ctx.symmetryPlanes[key] = group;
    }
    this.fillSymmetryPlanes(center, size);
  }

  /**
   * Sizes and centres the planes on the model as the UCS turns it. They lie on
   * the global planes the optimizer mirrors about, so a turn changes which of
   * the model's extents each one spans.
   */
  fitSymmetryPlanes(): void {
    if (!this.ctx.symmetryPlanes.xy) return;
    const { center, size } = this.ctx.ucsFrameObjectInfo;
    this.fillSymmetryPlanes(center, size);
  }

  private fillSymmetryPlanes(center: Vector3, size: Vector3): void {
    const { xy, xz, yz } = this.ctx.symmetryPlanes;

    this.fillPlane(xy, center, [size.x, size.y], undefined, undefined, [
      [size.x / 2, -size.y / 2, 0, size.x / 2, size.y / 2, 0],
      [-size.x / 2, -size.y / 2, 0, -size.x / 2, size.y / 2, 0],
      [-size.x / 2, -size.y / 2, 0, size.x / 2, -size.y / 2, 0],
      [-size.x / 2, size.y / 2, 0, size.x / 2, size.y / 2, 0]
    ]);

    this.fillPlane(xz, center, [size.x, size.z], Math.PI * 0.5, undefined, [
      [size.x / 2, 0, -size.z / 2, size.x / 2, 0, size.z / 2],
      [-size.x / 2, 0, -size.z / 2, -size.x / 2, 0, size.z / 2],
      [-size.x / 2, 0, -size.z / 2, size.x / 2, 0, -size.z / 2],
      [-size.x / 2, 0, size.z / 2, size.x / 2, 0, size.z / 2]
    ]);

    this.fillPlane(yz, center, [size.y, size.z], Math.PI * 0.5, Math.PI * 0.5, [
      [0, size.y / 2, -size.z / 2, 0, size.y / 2, size.z / 2],
      [0, -size.y / 2, -size.z / 2, 0, -size.y / 2, size.z / 2],
      [0, -size.y / 2, -size.z / 2, 0, size.y / 2, -size.z / 2],
      [0, -size.y / 2, size.z / 2, 0, size.y / 2, size.z / 2]
    ]);
  }

  /** Replaces a plane group's contents; the group itself keeps its visibility and parent. */
  private fillPlane(
    group: Group | undefined,
    center: Vector3,
    planeSize: [number, number],
    rotateX: number | undefined,
    rotateZ: number | undefined,
    linePositions: number[][]
  ): void {
    if (!group) return;
    DrillBitsManager.disposeContents(group);

    const lineMaterial = new LineMaterial({ color: 0x000000, linewidth: 3 });
    lineMaterial.resolution.set(
      this.ctx.canvasWidth,
      this.ctx.canvasHeight
    );
    // Basic, not Standard: these are flat guide planes sized to the whole
    // model, drawn DoubleSide and transparent. As a PBR material each one
    // shaded both faces with lighting and the scene envMap over most of the
    // viewport -- three planes of near-fullscreen overdraw for a surface
    // that is plain 50% black either way.
    const planeMaterial = new MeshBasicMaterial({
      color: 0x000000,
      side: DoubleSide,
      transparent: true,
      opacity: 0.5,
      // Guides should not occlude each other's blending where they cross.
      depthWrite: false
    });

    const linesGroup = new Group();

    const geometry = new PlaneGeometry(planeSize[0], planeSize[1], 1, 1);
    if (rotateX !== undefined) geometry.rotateX(rotateX);
    if (rotateZ !== undefined) geometry.rotateZ(rotateZ);
    const mesh = new Mesh(geometry, planeMaterial);

    for (const positions of linePositions) {
      const lineGeometry = new LineGeometry();
      lineGeometry.setPositions(positions);
      const line = new Line2(lineGeometry, lineMaterial);
      line.computeLineDistances();
      line.scale.set(1, 1, 1);
      linesGroup.add(line);
    }

    group.add(mesh, linesGroup);
    group.position.copy(center);
  }

  private millingDepthResolver: MillingDepthPlaneResolver | null = null;
  private millingDepthGroup: Group | null = null;

  /**
   * Shows the max milling depth planes, placed by `resolve` from the design
   * region's bounds (design minus non-design); null removes them. They are placed again whenever the UCS
   * turns the model, since that changes the bounds.
   */
  showMillingDepthPlanes(resolve: MillingDepthPlaneResolver | null): void {
    this.millingDepthResolver = resolve;
    this.fitMillingDepthPlanes();
  }

  fitMillingDepthPlanes(): void {
    const group = this.millingDepthGroup;
    if (group) {
      DrillBitsManager.disposeContents(group);
      group.removeFromParent();
      this.millingDepthGroup = null;
    }
    if (!this.millingDepthResolver) return;

    const info = this.ctx.designRegionUcsFrameInfo;
    if (!info) return;
    const specs = this.millingDepthResolver(info.box);
    if (specs.length === 0) return;

    const next = new Group();
    next.name = 'millingDepthPlanes';
    // A little wider than the part, so the plane's edge reads clear of the part's own edges.
    const pad = Math.max(info.size.x, info.size.y, info.size.z) * 0.04;
    for (const spec of specs) next.add(this.buildMillingDepthPlane(spec, info.box, pad));
    this.ctx.ucsFrame.add(next);
    this.millingDepthGroup = next;
  }

  private buildMillingDepthPlane(spec: MillingDepthPlaneSpec, box: Box3, pad: number): Group {
    const { axis, location } = spec;
    // The two in-plane axes, in right-handed order so the outline runs round the rectangle.
    const u = ((axis + 1) % 3) as 0 | 1 | 2;
    const v = ((axis + 2) % 3) as 0 | 1 | 2;
    const min = box.min.toArray();
    const max = box.max.toArray();
    const corner = (a: number, b: number): Vector3 => {
      const p: [number, number, number] = [0, 0, 0];
      p[axis] = location;
      p[u] = a;
      p[v] = b;
      return new Vector3(...p);
    };
    const [u0, u1, v0, v1] = [min[u] - pad, max[u] + pad, min[v] - pad, max[v] + pad];
    const corners = [corner(u0, v0), corner(u1, v0), corner(u1, v1), corner(u0, v1)];

    const color = FRAME_AXIS_COLORS[axis];
    const geometry = new BufferGeometry().setFromPoints([corners[0], corners[1], corners[2], corners[0], corners[2], corners[3]]);
    const mesh = new Mesh(
      geometry,
      // Flat, unlit and see-through like the symmetry planes, tinted by the axis it cuts.
      new MeshBasicMaterial({ color, side: DoubleSide, transparent: true, opacity: 0.25, depthWrite: false })
    );

    const lineMaterial = new LineMaterial({ color, linewidth: 2 });
    lineMaterial.resolution.set(this.ctx.canvasWidth, this.ctx.canvasHeight);
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([...corners, corners[0]].flatMap((c) => c.toArray()));
    const outline = new Line2(lineGeometry, lineMaterial);
    outline.computeLineDistances();

    const plane = new Group();
    plane.add(mesh, outline);
    return plane;
  }

  private static disposeContents(group: Group): void {
    group.traverse((child) => {
      const object = child as Mesh;
      object.geometry?.dispose();
      const material = object.material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
    group.clear();
  }

  disposeDrillBits(): void {
    const bits = this.ctx.drillBits;
    if (bits) {
      bits.x1 && (bits.x1.visible = false);
      bits.x2 && (bits.x2.visible = false);
      bits.y1 && (bits.y1.visible = false);
      bits.y2 && (bits.y2.visible = false);
      bits.z1 && (bits.z1.visible = false);
      bits.z2 && (bits.z2.visible = false);
    }
  }

  disposeSymmetryPlanes(): void {
    const sp = this.ctx.symmetryPlanes;
    if (sp.xy) sp.xy.visible = false;
    if (sp.xz) sp.xz.visible = false;
    if (sp.yz) sp.yz.visible = false;
  }

  async initDrillBits(): Promise<void> {
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync('assets/webgl/drillbit.glb');
    const drillbitMesh: Mesh = gltf.scene.getObjectByProperty('type', 'Mesh') as Mesh;

    if (!drillbitMesh) return;

    drillbitMesh.geometry.center();
    drillbitMesh.geometry.computeBoundingBox();
    drillbitMesh.updateMatrixWorld(true);
    const material = new MeshPhysicalMaterial({
      color: 0xffffff,
      side: DoubleSide,
      vertexColors: false,
      flatShading: false,
      opacity: 1,
      transparent: true,
      metalness: 1,
      roughness: 0.2,
      envMap: (this.ctx as unknown as Record<string, unknown>)['envMap'] as Texture,
      envMapIntensity: 0.7
    });
    drillbitMesh.material = material;

    let ratio = 1;
    const objects = this.ctx.objects as { group: Group }[];
    if (objects.length > 0 && parseFloat(this.ctx.session.project.minimumToolSize as unknown as string) === 0) {
      try {
        const bboxDesign = calculateBoundingBox(objects.map((o) => o.group));
        const voxNum = 600000;
        const feSize = Math.round(
          Number.parseFloat((VoxelGridCalculator.getFeatureSize(bboxDesign, 3, voxNum) * 0.8).toFixed(2))
        );
        ratio = feSize;
        this.ctx.session.project.minimumToolSize = ratio;
      } catch (_) {
        // Use default ratio
      }
    }

    drillbitMesh.geometry.scale(ratio, ratio, ratio);
    this.ctx.drillbitMesh = drillbitMesh;

    const bits = this.ctx.drillBits;
    for (const key of Object.keys(bits)) {
      bits[key as keyof typeof bits] = drillbitMesh.clone(true);
      bits[key as keyof typeof bits]!.visible = false;
    }

    this.ctx.session.project.minimumToolSize ??= bits.x1!.scale.x;

    if (this.ctx.session.project.minimumToolSize) {
      this.drillBitScaleChange(this.ctx.session.project.minimumToolSize);
    }

    bits.x1!.rotateZ(-Math.PI / 2);
    bits.x2!.rotateZ(Math.PI / 2);
    bits.z1!.rotateX(Math.PI / 2);
    bits.z2!.rotateX(-Math.PI / 2);
    bits.y1!.rotateZ(0);
    bits.y2!.rotateZ(Math.PI);

    this.drillBitUpdate();
  }

  addDrillBitsToOutline(): void {
    if (!this.ctx.helpersVisible) return;
    for (const bit of Object.values(this.ctx.drillBits)) {
      if (!bit) continue;
      this.ctx.ucsFrame.add(bit);
      this.ctx.outlineEffect.selection.add(bit);
    }
  }

  removeDrillBitsFromOutline(): void {
    this.ctx.outlineEffect.selection.clear();
  }

  drillBitUpdate(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tweens = (this.ctx as unknown as Record<string, unknown>)['tweens'] as TWEEN.Tween<any>[];
    for (const tween of tweens) {
      tween.stop();
    }
    tweens.length = 0;

    const bits = this.ctx.drillBits;
    const { center, size } = this.ctx.ucsFrameObjectInfo;
    const sizex = this.sizeInHolder(bits.x1!);
    const sizey = this.sizeInHolder(bits.y1!);
    const sizez = this.sizeInHolder(bits.z1!);

    bits.x1!.position.set(center.x - size.x / 2 - sizex.x / 2, center.y, center.z);
    bits.x2!.position.set(center.x + size.x / 2 + sizex.x / 2, center.y, center.z);
    bits.y1!.position.set(center.x, center.y - size.y / 2 - sizey.y / 2, center.z);
    bits.y2!.position.set(center.x, center.y + size.y / 2 + sizey.y / 2, center.z);
    bits.z1!.position.set(center.x, center.y, center.z - size.z / 2 - sizez.z / 2);
    bits.z2!.position.set(center.x, center.y, center.z + size.z / 2 + sizez.z / 2);

    // Position tweens
    const positionTweenData: [Mesh, string, number][] = [
      [bits.x1!, 'x', bits.x1!.position.x - 20],
      [bits.x2!, 'x', bits.x2!.position.x + 20],
      [bits.y1!, 'y', bits.y1!.position.y - 20],
      [bits.y2!, 'y', bits.y2!.position.y + 20],
      [bits.z1!, 'z', bits.z1!.position.z - 20],
      [bits.z2!, 'z', bits.z2!.position.z + 20]
    ];
    for (const [mesh, axis, target] of positionTweenData) {
      tweens.push(
        new Tween(mesh.position)
          .to({ [axis]: target }, 1000)
          .easing(Easing.Cubic.InOut)
          .yoyo(true)
          .repeat(9999999999)
          .start()
      );
    }

    // Rotation tweens
    const rotationTweenData: [Mesh, string, number][] = [
      [bits.x1!, 'x', bits.x1!.rotation.x + 4],
      [bits.x2!, 'x', bits.x2!.rotation.x - 4],
      [bits.y1!, 'y', bits.y1!.rotation.y + 4],
      [bits.y2!, 'y', bits.y2!.rotation.y - 4],
      [bits.z1!, 'y', bits.z1!.rotation.y + 4],
      [bits.z2!, 'y', bits.z2!.rotation.y + 4]
    ];
    for (const [mesh, axis, target] of rotationTweenData) {
      tweens.push(
        new Tween(mesh.rotation)
          .to({ [axis]: target }, 1000)
          .yoyo(false)
          .repeat(9999999999)
          .start()
      );
    }
  }

  /** A bit's size in the coordinates of the UCS-frame holder it sits in, whatever turn the holder carries. */
  private sizeInHolder(bit: Mesh): Vector3 {
    if (!bit.geometry.boundingBox) bit.geometry.computeBoundingBox();
    bit.updateMatrix();
    return bit.geometry.boundingBox!.clone().applyMatrix4(bit.matrix).getSize(new Vector3());
  }

    scaleMeshToDiameter(mesh: Mesh, desiredDiameter: number): number {
    const boundingBox = new Box3().setFromObject(mesh);
    const currentSize = new Vector3();
    boundingBox.getSize(currentSize);
    const currentDiameter = Math.max(currentSize.x, currentSize.z);
    return desiredDiameter / currentDiameter;
  }

  drillBitScaleChange(diameter: number): void {
    const bits = this.ctx.drillBits;
    const refMesh = this.ctx.drillbitMesh as Mesh;
    for (const key of Object.keys(bits)) {
      const d = bits[key as keyof typeof bits] as Mesh;
      const scaleFactor = this.scaleMeshToDiameter(refMesh, diameter);
      d.scale.set(scaleFactor, scaleFactor, scaleFactor);
    }
    this.drillBitUpdate();
  }
}
